/**
 * React Query wrapper for the AI Coach chat on a single OKR.
 * - Loads the caller's conversation.
 * - askQuestion is optimistic AND streaming: pushes a transient user row, then
 *   replaces it with the server-issued row when the SSE `user-saved` event
 *   arrives, appends/extends an assistant row as `delta` events stream in,
 *   then finalizes both rows on `done`. On error the optimistic user row is
 *   marked `failed` and any partial assistant row is dropped.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  aiCoachChat,
  type AICoachMessageView,
} from '../services/aiCoach.service';

export type ChatMessage = AICoachMessageView & {
  status?: 'pending' | 'failed';   // present only on the optimistic user row
  clientId?: string;               // stable id before the server returns
  isStreaming?: boolean;           // assistant row while deltas are still arriving
};

export const aiCoachChatKeys = {
  all: ['ai-coach-chat'] as const,
  okr: (okrId: string) => [...aiCoachChatKeys.all, okrId] as const,
};

const cryptoRandomId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `c-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const msUntilNextUtcMidnight = () => {
  const now = new Date();
  const next = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0, 0, 0, 0
  );
  return next - now.getTime();
};

export const useAICoachChat = (okrId: string) => {
  const qc = useQueryClient();
  const key = aiCoachChatKeys.okr(okrId);
  const [capReached, setCapReached] = useState(false);
  const [isAsking, setIsAsking] = useState(false);
  // Latest in-flight request, so unmount can hang up.
  const abortRef = useRef<AbortController | null>(null);

  // Auto-clear the cap-reached banner at the next 00:00 UTC reset.
  useEffect(() => {
    if (!capReached) return;
    const t = setTimeout(() => setCapReached(false), msUntilNextUtcMidnight());
    return () => clearTimeout(t);
  }, [capReached]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const query = useQuery({
    queryKey: key,
    queryFn: () => aiCoachChat.listMessages(okrId),
    staleTime: 1000 * 30,
  });

  const ask = useCallback(
    async (question: string): Promise<void> => {
      const clientId = cryptoRandomId();
      const streamingId = `stream-${clientId}`;

      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ChatMessage[]>(key) ?? [];
      const optimisticUser: ChatMessage = {
        id: clientId,
        clientId,
        role: 'user',
        content: question,
        createdAt: new Date().toISOString(),
        tokensUsed: null,
        model: null,
        status: 'pending',
      };
      qc.setQueryData<ChatMessage[]>(key, [...prev, optimisticUser]);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsAsking(true);

      try {
        await aiCoachChat.askQuestionStream(okrId, question, {
          signal: controller.signal,

          onUserSaved: (userMsg) => {
            // Replace the optimistic placeholder with the server-issued user
            // row but preserve clientId — we still need it as a handle in case
            // the stream fails partway through.
            const list = qc.getQueryData<ChatMessage[]>(key) ?? [];
            qc.setQueryData<ChatMessage[]>(
              key,
              list.map((m) => (m.clientId === clientId ? { ...userMsg, clientId } : m))
            );
          },

          onDelta: (text) => {
            const list = qc.getQueryData<ChatMessage[]>(key) ?? [];
            const idx = list.findIndex((m) => m.clientId === streamingId);
            if (idx === -1) {
              const newAssistant: ChatMessage = {
                id: streamingId,
                clientId: streamingId,
                role: 'assistant',
                content: text,
                createdAt: new Date().toISOString(),
                tokensUsed: null,
                model: null,
                isStreaming: true,
              };
              qc.setQueryData<ChatMessage[]>(key, [...list, newAssistant]);
            } else {
              const next = list.slice();
              next[idx] = { ...next[idx], content: next[idx].content + text };
              qc.setQueryData<ChatMessage[]>(key, next);
            }
          },

          onDone: (assistantMsg) => {
            const list = qc.getQueryData<ChatMessage[]>(key) ?? [];
            const next = list.map<ChatMessage>((m) => {
              if (m.clientId === streamingId) return { ...assistantMsg };
              if (m.clientId === clientId) {
                // Strip clientId/status now that the round-trip succeeded.
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { clientId: _c, status: _s, isStreaming: _i, ...clean } = m;
                return clean as ChatMessage;
              }
              return m;
            });
            qc.setQueryData<ChatMessage[]>(key, next);
          },

          onError: (e) => {
            const list = qc.getQueryData<ChatMessage[]>(key) ?? [];
            const next = list
              .filter((m) => m.clientId !== streamingId)
              .map<ChatMessage>((m) =>
                m.clientId === clientId ? { ...m, status: 'failed' } : m
              );
            qc.setQueryData<ChatMessage[]>(key, next);
            if (e.code === 'AI_CAP_REACHED') {
              setCapReached(true);
              return;
            }
            toast.error(e.message || 'Failed to ask the coach');
          },
        });
      } finally {
        setIsAsking(false);
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [okrId, qc, key]
  );

  const clearMutation = useMutation({
    mutationFn: () => aiCoachChat.clearMessages(okrId),
    onSuccess: () => {
      qc.setQueryData(key, [] as ChatMessage[]);
      toast.success('Conversation cleared');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to clear conversation'),
  });

  // Drop the failed bubble before re-asking so the same question doesn't render
  // twice in the transcript.
  const retry = useCallback(
    (failed: ChatMessage) => {
      if (!failed.clientId) return ask(failed.content);
      const failedId = failed.clientId;
      qc.setQueryData<ChatMessage[]>(key, (list) =>
        (list ?? []).filter((m) => m.clientId !== failedId)
      );
      return ask(failed.content);
    },
    [ask, qc, key]
  );

  return {
    messages: (query.data ?? []) as ChatMessage[],
    isLoading: query.isLoading,
    error: query.error,
    ask,
    retry,
    isAsking,
    clear: () => clearMutation.mutateAsync().then(() => undefined),
    isClearing: clearMutation.isPending,
    capReached,
  };
};
